/**
 * RestaurantAiChatWidget — floating chat for the dining room AI
 * receptionist. Sibling of TackleAiChatWidget.
 *
 * Drop on any customer-facing restaurant page:
 *   <RestaurantAiChatWidget />
 *
 * Reads the diner's saved allergen card via DietaryProfileContext
 * and forwards `customer_email` to the backend so the LLM never
 * suggests an item that clashes.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDietaryProfile } from "@/context/DietaryProfileContext";
import { useBusiness } from "@/context/BusinessContext";
import { MessageCircle, X, Send, ChefHat, UserRound, Mic, Volume2, Loader2 } from "lucide-react";
import { useTts, useWhisperDictation } from "@/lib/useVoice";

const SID_KEY = "hp_restaurant_ai_sid_v1";

export default function RestaurantAiChatWidget() {
  const { user } = useAuth();
  const { profile: card } = useDietaryProfile();
  const { business } = useBusiness();

  const [open, setOpen] = useState(false);
  const [sid, setSid]   = useState(() => {
    try { return localStorage.getItem(SID_KEY) || ""; }
    catch { return ""; }
  });
  const [turns, setTurns] = useState(() => {
    const intro = card?.allergens?.length > 0
      ? "Bonsoir! I've got your allergen card up — I'll only "
        + "recommend dishes that are safe for you. What are "
        + "you in the mood for?"
      : "Bonsoir! Welcome — I can talk you through the menu, "
        + "book a table, or suggest wines for tonight. What "
        + "can I help with?";
    return [{ role: "bot", text: intro }];
  });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [handoffSent, setHandoffSent] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const panelRef = useRef(null);

  const { speak, stop: stopSpeaking, speaking } = useTts();
  const { listening, transcribing, transcript,
              start: startDict, stop: stopDict,
              supported: sttSupported } = useWhisperDictation();
  useEffect(() => { if (transcript) setMsg(transcript); }, [transcript]);

  useEffect(() => {
    requestAnimationFrame(() => {
      const el = panelRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, [turns]);

  async function send() {
    const text = msg.trim();
    if (!text || busy) return;
    setMsg("");
    setTurns((t) => [...t, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/restaurant/ai/chat", {
        session_id:     sid || null,
        message:        text,
        customer_email: user?.email || null,
        business_id:    business?.id || null,
      });
      if (!sid && data.session_id) {
        setSid(data.session_id);
        try { localStorage.setItem(SID_KEY, data.session_id); }
        catch { /* */ }
      }
      setTurns((t) => [...t, {
        role: "bot",
        text: data.reply,
        needsHuman: !!data.needs_human,
        michelin:   data.michelin_picks || [],
      }]);
      if (voiceOn && data.reply) {
        speak(data.reply);
      }
    } catch (e) {
      setTurns((t) => [...t, {
        role: "bot",
        text: "Sorry, hit a snag: " +
              (formatApiError(e.response?.data?.detail) || e.message),
      }]);
    } finally { setBusy(false); }
  }

  async function handoff() {
    if (!sid) return;
    setBusy(true);
    try {
      await api.post("/restaurant/ai/handoff", {
        session_id:     sid,
        reason:         "Diner requested a human waiter",
        customer_email: user?.email || null,
        customer_name:  user?.name  || null,
        business_id:    business?.id || null,
      });
      setHandoffSent(true);
      setTurns((t) => [...t, {
        role: "system",
        text: "✓ A real waiter will be with you shortly. "
              + "Anything else in the meantime?",
      }]);
    } catch (e) {
      setTurns((t) => [...t, {
        role: "bot",
        text: "Couldn't queue the handoff: " + e.message,
      }]);
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button data-testid="restaurant-ai-open"
              onClick={() => setOpen(true)}
              className="fixed bottom-6 left-6 z-50 bg-[#5A7247]
                          hover:bg-[#4A5E3A] text-white rounded-full
                          h-14 px-5 shadow-lg flex items-center gap-2
                          font-semibold">
        <ChefHat className="h-5 w-5" />
        Ask the Maître'D
      </button>
    );
  }

  return (
    <div data-testid="restaurant-ai-panel"
            className="fixed bottom-6 left-6 z-50 w-[360px] max-w-[92vw]
                        h-[520px] max-h-[80vh] bg-white border
                        border-stone-200 rounded-2xl shadow-2xl
                        flex flex-col overflow-hidden">
      <div className="bg-[#5A7247] text-white px-4 py-3 flex
                          items-center justify-between">
        <div className="flex items-center gap-2">
          <ChefHat className="h-5 w-5" />
          <div>
            <div className="text-sm font-bold">Maître'D AI</div>
            <div className="text-[10px] opacity-80">
              {card?.allergens?.length > 0
                ? `Allergen-safe · hiding ${card.allergens.join(", ")}`
                : "Menu · bookings · wines"}
            </div>
          </div>
        </div>
        <button data-testid="restaurant-ai-close"
                onClick={() => setOpen(false)}>
          <X className="h-5 w-5" />
        </button>
      </div>

      <div ref={panelRef}
              className="flex-1 overflow-y-auto p-3 space-y-2 bg-stone-50">
        {turns.map((t, i) => (
          <div key={i}
                  className={`flex gap-2 ${
                    t.role === "user" ? "justify-end" : "justify-start"}`}>
            {t.role === "bot" && <ChefHat className="h-5 w-5 text-[#5A7247] shrink-0 mt-1" />}
            {t.role === "system" && <div className="w-5" />}
            <div className={`max-w-[80%] text-sm rounded-2xl px-3 py-2
                              whitespace-pre-wrap ${
                                t.role === "user"
                                  ? "bg-[#5A7247] text-white"
                                  : t.role === "system"
                                  ? "bg-amber-50 border border-amber-200 text-amber-900"
                                  : "bg-white border border-stone-200"}`}>
              {t.text}
              {t.michelin?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5"
                        data-testid={`restaurant-ai-michelin-${i}`}>
                  {t.michelin.slice(0, 3).map((name) => (
                    <span key={name}
                              className="text-[11px] bg-amber-50
                                              text-amber-800 border border-amber-300
                                              rounded-full px-2 py-0.5">
                      ⭐ {name}
                    </span>
                  ))}
                </div>
              )}
              {t.needsHuman && !handoffSent && (
                <button data-testid={`restaurant-ai-escalate-${i}`}
                        onClick={handoff}
                        className="mt-2 text-[11px] underline
                                        text-rose-700 font-semibold">
                  Need a real waiter? Tap to call one.
                </button>
              )}
            </div>
            {t.role === "user" && <UserRound className="h-5 w-5 text-stone-500 shrink-0 mt-1" />}
          </div>
        ))}
        {busy && (
          <div className="text-xs text-stone-500 italic"
                  data-testid="restaurant-ai-thinking">
            …consulting the chef
          </div>
        )}
      </div>

      <div className="border-t border-stone-200 p-2 flex gap-1">
        {sttSupported && (
          <button onMouseDown={startDict} onMouseUp={stopDict}
                    onTouchStart={startDict} onTouchEnd={stopDict}
                    data-testid="restaurant-ai-mic"
                    title={transcribing ? "Transcribing…" : "Hold to speak"}
                    disabled={transcribing}
                    className={`rounded-full w-10 h-10 flex
                                  items-center justify-center border
                                  ${listening
                                      ? "bg-rose-100 border-rose-400 text-rose-700 animate-pulse"
                                      : transcribing
                                      ? "bg-amber-50 border-amber-300 text-amber-700"
                                      : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}>
            {transcribing
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Mic className="h-4 w-4" />}
          </button>
        )}
        <button onClick={() => {
                  if (speaking) stopSpeaking();
                  else setVoiceOn((v) => !v);
                }}
                data-testid="restaurant-ai-voice-toggle"
                title={voiceOn ? "Voice replies ON" : "Voice replies OFF"}
                className={`rounded-full w-10 h-10 flex items-center
                                justify-center border ${
                                  voiceOn
                                    ? "bg-[#E7EFD8] border-[#5A7247] text-[#5A7247]"
                                    : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}>
          <Volume2 className="h-4 w-4" />
        </button>
        <input value={msg} onChange={(e) => setMsg(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="restaurant-ai-input"
                  placeholder="Ask about the menu, dietary or a booking…"
                  disabled={busy}
                  className="flex-1 px-3 py-2 border border-stone-300
                              rounded-full text-sm focus:outline-none
                              focus:border-[#5A7247]" />
        <button onClick={send} disabled={busy || !msg.trim()}
                  data-testid="restaurant-ai-send"
                  className="bg-[#5A7247] hover:bg-[#4A5E3A]
                              disabled:opacity-40 text-white rounded-full
                              w-10 h-10 flex items-center justify-center">
          <Send className="h-4 w-4" />
        </button>
      </div>
      <button onClick={handoff} disabled={handoffSent || !sid || busy}
              data-testid="restaurant-ai-call-waiter"
              className="border-t border-stone-200 px-3 py-2 text-xs
                          text-stone-600 hover:bg-stone-50
                          disabled:opacity-50">
        {handoffSent ? "✓ Real waiter on their way"
                              : "Call a real waiter →"}
      </button>
    </div>
  );
}
